// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-lines */
import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { WheelTimePicker } from "@src/appV2/redesign/components/WheelTimePicker/WheelTimePicker";
import type { UseModalState } from "@src/appV2/redesign/system/ui-components";
import { isAfter, isBefore, startOfMinute } from "date-fns";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { formatTimeWithTimezone } from "../../../utils/formatTimeWithTimezone";
import { isBoundaryError } from "../analytics/isBoundaryError";
import { useClockOutTimeValidation } from "../hooks/useClockOutTimeValidation";
import {
  getBottomSheetIntent,
  TimekeepingAdjustmentBottomSheet,
} from "../TimekeepingAdjustmentBottomSheet";
import { getDayIndicator } from "./utils/getDayIndicator";
import { inferDateFromTime } from "./utils/inferDateFromTime";
import { getLocalTimeForPicker, getUtcTimeFromPicker } from "./utils/timezoneConversion";

interface EditClockOutTimeFormValues {
  clockOutTime?: Date;
}

interface EditClockOutTimeBottomSheetProps {
  modalState: UseModalState;
  clockOutTime?: Date;
  workplaceTimezone: string;
  clockOutButtonTappedAt?: Date;
  breakEndTime?: Date;
  clockInTime?: Date;
  shiftStartAt: Date;
  shiftEndAt: Date;
  geofenceDepartureTime?: Date;
  latestPlausibleDepartureTime?: Date;
  workerClockOut?: Date;
  isGeofenceFacility?: boolean;
  isNfcOrMeshFacility?: boolean;
  isDirectIntegrationFacility?: boolean;
  allowTimeAdjustmentRequest: boolean;
  lateClockOutEnabled: boolean;
  isShiftExtendable?: boolean;
  shouldShowApprovalStep?: boolean;
  isEnforceGeofenceTimestampsEnabled?: boolean;
  verifiedClockOut?: Date;
  shiftId?: string;
  workerId?: string;
  workplaceId?: string;
  timesheetFlow?: string;
  existingReason?: string;
  onConfirm: (clockOutTime: Date, reason: string) => void;
  onCancel?: () => void;
}

export function EditClockOutTimeBottomSheet(props: EditClockOutTimeBottomSheetProps) {
  const {
    modalState,
    clockOutTime,
    workplaceTimezone,
    clockOutButtonTappedAt,
    breakEndTime,
    clockInTime,
    shiftStartAt,
    shiftEndAt,
    geofenceDepartureTime,
    latestPlausibleDepartureTime,
    workerClockOut,
    isGeofenceFacility = false,
    isNfcOrMeshFacility = false,
    isDirectIntegrationFacility = false,
    allowTimeAdjustmentRequest,
    lateClockOutEnabled,
    isShiftExtendable = false,
    shouldShowApprovalStep = false,
    isEnforceGeofenceTimestampsEnabled = false,
    verifiedClockOut,
    shiftId,
    workerId,
    workplaceId,
    timesheetFlow,
    existingReason,
    onConfirm,
    onCancel,
  } = props;

  const [saveAttempted, setSaveAttempted] = useState(false);
  const [isPickerScrolling, setIsPickerScrolling] = useState(false);

  const { setValue, watch, reset } = useForm<EditClockOutTimeFormValues>({
    defaultValues: {
      clockOutTime: isDefined(clockOutTime)
        ? getLocalTimeForPicker(clockOutTime, workplaceTimezone)
        : undefined,
    },
  });

  useEffect(() => {
    if (!modalState.modalIsOpen) {
      const resetValue = isDefined(clockOutTime)
        ? getLocalTimeForPicker(clockOutTime, workplaceTimezone)
        : undefined;
      reset({ clockOutTime: resetValue });
      setSaveAttempted(false);
    }
  }, [modalState.modalIsOpen, clockOutTime, reset, workplaceTimezone]);

  const currentLocalClockOutTime = watch("clockOutTime");

  const currentClockOutTime = isDefined(currentLocalClockOutTime)
    ? getUtcTimeFromPicker(currentLocalClockOutTime, workplaceTimezone)
    : undefined;

  // --- New validation via hook (when MPR flag is on) ---
  const clockOutValidation = useClockOutTimeValidation({
    selectedTime: currentClockOutTime,
    shiftId: shiftId ?? "",
    shiftEndAt,
    workplaceTimezone,
    clockInTime,
    geofenceDepartureTime,
    latestPlausibleDepartureTime,
    isEnforceGeofenceTimestampsEnabled: isEnforceGeofenceTimestampsEnabled ?? false,
    preventLateClockOut: !lateClockOutEnabled,
    verifiedClockOut,
    workerClockOut,
    isGeofenceWorkplace: isGeofenceFacility,
    isNfcOrMeshWorkplace: isNfcOrMeshFacility,
    isDirectIntegrationWorkplace: isDirectIntegrationFacility,
    suppressMissedPunchWarning: false,
  });

  // --- Legacy validation (when MPR flag is off) ---
  const getLegacyValidationError = (selectedTime: Date): string | undefined => {
    if (isDefined(clockOutButtonTappedAt) && isAfter(selectedTime, clockOutButtonTappedAt)) {
      const formattedClockOutButtonTappedAt = formatTimeWithTimezone(
        clockOutButtonTappedAt,
        workplaceTimezone,
        false
      );
      if (isShiftExtendable) {
        return `You can only select a clock out time at or before ${formattedClockOutButtonTappedAt} — as you did not complete a Shift Extension form. Please proceed with this timesheet and request corrections as a pay adjustment in your Shift History.`;
      }

      return `Please select a clock out time at or before ${formattedClockOutButtonTappedAt} — the time you tapped 'Clock Out'`;
    }

    if (isDefined(breakEndTime) && isBefore(selectedTime, breakEndTime)) {
      return "You cannot clock out before your break ends.";
    }

    if (isDefined(clockInTime) && !isAfter(selectedTime, clockInTime)) {
      return "You cannot clock out before or at your clock in time.";
    }

    if (!lateClockOutEnabled && isAfter(startOfMinute(selectedTime), startOfMinute(shiftEndAt))) {
      return "You cannot edit the clock out time past scheduled clock out. If you stayed past your clock out time, you can request a pay adjustment after the shift.";
    }

    return undefined;
  };

  const getValidationState = () => {
    if (allowTimeAdjustmentRequest) {
      return {
        errorMessage: clockOutValidation.errorMessage,
        errorType: clockOutValidation.errorType,
        geofenceValidationType: clockOutValidation.geofenceValidationType,
        validationIntent: clockOutValidation.intent,
        validationIntentContent: clockOutValidation.intentContent,
        validationIntentIcon: clockOutValidation.intentIcon,
      };
    }

    const legacyError = isDefined(currentClockOutTime)
      ? getLegacyValidationError(currentClockOutTime)
      : undefined;
    const legacyIntent =
      saveAttempted && isDefined(legacyError) ? getBottomSheetIntent("error") : undefined;

    return {
      errorMessage: legacyError,
      errorType: undefined,
      geofenceValidationType: undefined,
      validationIntent: legacyIntent?.intent,
      validationIntentContent: saveAttempted && isDefined(legacyError) ? legacyError : undefined,
      validationIntentIcon: legacyIntent?.intentIcon,
    };
  };

  const {
    errorMessage,
    errorType,
    geofenceValidationType,
    validationIntent,
    validationIntentContent,
    validationIntentIcon,
  } = getValidationState();

  const handleTimeChange = (newTime: Date | undefined) => {
    if (isDefined(newTime)) {
      const localShiftEnd = getLocalTimeForPicker(shiftEndAt, workplaceTimezone);
      const inferResult = inferDateFromTime({
        timeInput: { hour: newTime.getHours(), minute: newTime.getMinutes() },
        shiftStart: getLocalTimeForPicker(shiftStartAt, workplaceTimezone),
        shiftEnd: localShiftEnd,
        fallbackAnchor: localShiftEnd,
      });

      const timeToSet = inferResult.success ? inferResult.date : newTime;
      setValue("clockOutTime", timeToSet, { shouldDirty: true });
      setSaveAttempted(false);
    }
  };

  const handlePickerConfirm = async (): Promise<boolean> => {
    setSaveAttempted(true);

    if (isBoundaryError(errorType)) {
      logEvent(APP_V2_USER_EVENTS.MISSED_PUNCH_REQUEST_TIMESHEET_BOUNDARY_VIOLATION_ATTEMPTED, {
        actor: "worker",
        clockAction: "clock-out",
        errorType,
        shiftId,
        workplaceId,
        selectedTime: currentClockOutTime?.toISOString(),
        geofenceDepartureTime: geofenceDepartureTime?.toISOString(),
        shiftEndAt: shiftEndAt?.toISOString(),
        verifiedClockOut: verifiedClockOut?.toISOString(),
        latestPlausibleDepartureTime: latestPlausibleDepartureTime?.toISOString(),
        geofenceValidationType,
      });
    }

    if (errorMessage) {
      logEvent(APP_V2_USER_EVENTS.TIMESHEET_FORM_VALIDATION_ERROR, {
        shiftId,
        workerId,
        workplaceId,
        error: errorMessage,
        errorType,
        timekeepingAction: "clock-out",
        timesheetFlow,
      });
      return false;
    }

    if (allowTimeAdjustmentRequest) {
      return clockOutValidation.validateOverlap();
    }

    return true;
  };

  const dayIndicator = getDayIndicator({
    selectedTimestamp: currentClockOutTime,
    referenceTimestamp: shiftEndAt,
    workplaceTimezone,
  });

  const requiresApproval =
    shouldShowApprovalStep &&
    isDefined(currentClockOutTime) &&
    isDefined(workerClockOut) &&
    isAfter(currentClockOutTime, workerClockOut);
  const showApprovalIntent = requiresApproval && !isPickerScrolling && !validationIntent;

  const isPickerValid = allowTimeAdjustmentRequest
    ? clockOutValidation.isValid && !clockOutValidation.isValidatingOverlap
    : isDefined(currentClockOutTime);

  return (
    <TimekeepingAdjustmentBottomSheet
      modalState={modalState}
      intent={validationIntent ?? (showApprovalIntent ? "neutral" : undefined)}
      intentContent={
        validationIntentContent ??
        (showApprovalIntent
          ? "You are requesting a later clock out time. No worries - you'll be able to add the reason on the next screen."
          : undefined)
      }
      intentIcon={validationIntentIcon ?? (showApprovalIntent ? "info" : undefined)}
      pickerTitle="Adjust clock out"
      hasChanges={isDefined(currentClockOutTime)}
      isPickerScrolling={isPickerScrolling}
      isPickerValid={isPickerValid}
      requiresApproval={requiresApproval}
      reasonTitle="Why did you adjust your clock out time?"
      existingReason={existingReason}
      reasonHelperText={
        existingReason
          ? "Edit your message to the workplace to explain why you adjusted your clock out time."
          : undefined
      }
      onPickerConfirm={handlePickerConfirm}
      onConfirm={(reason) => {
        if (isDefined(currentClockOutTime)) {
          onConfirm(currentClockOutTime, reason);
        }
      }}
      onCancel={onCancel}
    >
      <WheelTimePicker
        preventParentTouchStart
        value={currentLocalClockOutTime}
        dayIndicator={dayIndicator}
        containerSx={{
          alignSelf: "center",
        }}
        onChange={handleTimeChange}
        onScrollingChange={setIsPickerScrolling}
      />
    </TimekeepingAdjustmentBottomSheet>
  );
}
